import React, { useEffect, useState } from "react";
import { Button, Header, Icon, Table, Pagination, Segment, Label, Grid, Modal } from "semantic-ui-react";
import { toast } from "react-toastify";
import { RouteDto } from "../../../models/requests/route-dto";
import useRouteService from "../../../services/backoffice/hooks/useRouteService";
import { ToasterConfig } from "../../../config/ToasterConfig";
import TooltipInfo from "../../tooltip/tooltip";
import { SessionUser } from "../../../models/session-user";
import SessionStorage from "../../../store/session";
import RouteForm from "./create-route-form";
import { RouteFilter } from "../../../models/requests/route-filter";
import { Dropdown } from "semantic-ui-react";
import usePointOfInterestService from "../../../services/backoffice/hooks/usePointOfInterestService";

interface RouteListProps {
    isModal?: boolean;
    open?: boolean;
    onClose?: () => void;
}

const RouteList: React.FC<RouteListProps> = ({ isModal = false, open = false, onClose }) => {
    const [routes, setRoutes] = useState<RouteDto[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<RouteFilter>({ Page: 1 });
    const [totalPages, setTotalPages] = useState<number>(0);
    const { getRoutes, deleteRoute } = useRouteService();
    const { getPointsOfInterestShort } = usePointOfInterestService();
    const [sessionUser, setSessionUser] = useState<SessionUser>();
    const [showFilters, setShowFilters] = useState(false);
    const [pointsOfInterest, setPointsOfInterest] = useState<Array<{ key: string, text: string, value: string }>>([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [editRouteId, setEditRouteId] = useState<string | null>(null);

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    useEffect(() => {
        async function loadPointsOfInterest() {
            try {
                const points = await getPointsOfInterestShort();
                setPointsOfInterest(points.map(p => ({
                    key: p.key,
                    text: p.text,
                    value: p.value
                })));
            } catch (err) {
                console.error("Erro ao carregar pontos de interesse", err);
            }
        }
        loadPointsOfInterest();
    }, []);

    useEffect(() => {
        async function updateSessionUser() {
            const user = await SessionStorage.getSession();
            setSessionUser(user);
        }
        updateSessionUser();
    }, []);

    useEffect(() => {
        async function loadRoutes() {
            setLoading(true);
            try {
                const response = await getRoutes(filter);
                setRoutes(response.itens);
                setTotalPages(response.totalPages);
            } catch (err: any) {
                setError("Erro ao carregar rotas.");
            } finally {
                setLoading(false);
            }
        }
        loadRoutes();
    }, [filter]);

    const handleDelete = async (id: string) => {
        try {
            const result = window.confirm("Tem certeza que deseja excluir esta rota?");
            if (result) {
                await deleteRoute(id);
                toast.success("Rota excluída com sucesso", ToasterConfig.success);
                setRoutes(routes.filter((r) => r.id !== id));
            }
        } catch (err: any) {
            toast.error("Erro ao excluir rota", ToasterConfig.error);
        }
    };

    const handleEdit = (id: string) => {
        setEditRouteId(id);
        setModalOpen(true);
    };

    const formContent = (
        <Segment raised style={{ maxWidth: "900px", margin: "5px auto", padding: "20px" }}>
            <Header as="h3" textAlign="center" color={'green'}>
                Rotas
            </Header>
            <Table.Row>
                <Table.HeaderCell>
                    {sessionUser?.userData?.profile === 'Admin' && (
                        <Button color="green" onClick={() => setModalOpen(true)}>
                            <Icon name='plus' /> Nova Rota
                        </Button>
                    )}
                </Table.HeaderCell>
                <Table.HeaderCell colSpan="4">
                    <Button onClick={toggleFilters} color="green">
                        <Icon name={showFilters ? 'hide' : 'unhide'} />
                        {showFilters ? 'Ocultar filtros' : 'Exibir filtros'}
                    </Button>
                </Table.HeaderCell>
            </Table.Row>

            {showFilters && (
                <Segment>
                    <Grid stackable doubling padded>
                        <Grid.Row>
                            <Grid.Column width={6}>
                                <label>Origem</label>
                                <Dropdown
                                    fluid
                                    search
                                    selection
                                    clearable
                                    options={pointsOfInterest}
                                    placeholder="Selecione a origem"
                                    value={filter?.originId ?? ''}
                                    onChange={(_, data) => setFilter({ ...filter, originId: data.value as string })}
                                />
                            </Grid.Column>

                            <Grid.Column width={6}>
                                <label>Destino</label>
                                <Dropdown
                                    fluid
                                    search
                                    selection
                                    clearable
                                    options={pointsOfInterest}
                                    placeholder="Selecione o destino"
                                    value={filter?.destinationId ?? ''}
                                    onChange={(_, data) => setFilter({ ...filter, destinationId: data.value as string })}
                                />
                            </Grid.Column>

                            <Grid.Column width={2} verticalAlign="bottom">
                                <Button color="green" onClick={() => setFilter({ ...filter, Page: 1 })}>Buscar</Button>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Segment>
            )}

            <Table celled striped selectable color={"green"}>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>#</Table.HeaderCell>
                        <Table.HeaderCell>Origem</Table.HeaderCell>
                        <Table.HeaderCell>Destino</Table.HeaderCell>
                        <Table.HeaderCell>Ativo</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {routes?.map((route) => (
                        <Table.Row key={route.id}>
                            <Table.Cell>
                                <Button.Group>
                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content="Editar rota"
                                            trigger={
                                                <Button
                                                    color="green"
                                                    onClick={() => handleEdit(route.id)}
                                                    icon="edit"
                                                />
                                            }
                                        />
                                    )}

                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content="Excluir rota"
                                            trigger={
                                                <Button
                                                    color="red"
                                                    onClick={() => handleDelete(route.id)}
                                                    icon="trash"
                                                />
                                            }
                                        />
                                    )}
                                </Button.Group>
                            </Table.Cell>
                            <Table.Cell>{route.origin.name}</Table.Cell>
                            <Table.Cell>{route.destination.name}</Table.Cell>
                            <Table.Cell>
                                <Label color={route.isActive ? 'green' : 'red'}>
                                    {route.isActive ? 'Sim' : 'Não'}
                                </Label>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>

            <Pagination
                totalPages={totalPages}
                defaultActivePage={filter?.Page ?? 1}
                onPageChange={(e, { activePage }) => setFilter({ ...filter, Page: activePage as number })}
            />
        </Segment>
    );

    return (
        <>
            <Modal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                size="tiny"
                style={{ width: '680px', maxWidth: '95vw', padding: '20px' }}
            >
                <RouteForm
                    isModal
                    open={modalOpen}
                    routeId={editRouteId || undefined}
                    onClose={() => {
                        setModalOpen(false);
                        setEditRouteId(null);
                        setFilter({ ...filter });
                    }}
                />
            </Modal>

            {formContent}
        </>
    );
};

export default RouteList; 